import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { ComingSoonPage } from '@/features/placeholder/ComingSoonPage';

// Module werden per Route nachgeladen (Code-Splitting) — kleineres Initial-Bundle;
// der Router bleibt bis zum Laden auf der alten Seite (kein Flackern).
export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <DashboardPage /> },
      {
        path: 'analyse',
        lazy: async () => ({
          Component: (await import('@/features/analytics/AnalyticsPage')).AnalyticsPage,
        }),
      },
      {
        path: 'selektionspool',
        lazy: async () => ({
          Component: (await import('@/features/selektionspool/SelektionspoolPage')).SelektionspoolPage,
        }),
      },
      {
        path: 'suche',
        lazy: async () => ({
          Component: (await import('@/features/search/SearchResultsPage')).SearchResultsPage,
        }),
      },
      {
        path: 'bereich/:slug',
        lazy: async () => ({
          Component: (await import('@/features/overview/NavGroupHubPage')).NavGroupHubPage,
        }),
      },
      {
        path: 'projekte',
        lazy: async () => ({
          Component: (await import('@/features/projects/ProjectsPage')).ProjectsPage,
        }),
      },
      {
        path: 'projekte/analyse',
        lazy: async () => ({
          Component: (await import('@/features/projects/ProjectsAnalyticsPage')).ProjectsAnalyticsPage,
        }),
      },
      {
        path: 'zeitplan',
        lazy: async () => ({
          Component: (await import('@/features/projects/schedule/SchedulePage')).SchedulePage,
        }),
      },
      {
        path: 'qm',
        lazy: async () => ({
          Component: (await import('@/features/qm/QmListPage')).QmListPage,
        }),
      },
      {
        path: 'qm/:id',
        lazy: async () => ({
          Component: (await import('@/features/qm/QmDetailPage')).QmDetailPage,
        }),
      },
      {
        path: 'projekte/:id',
        lazy: async () => ({
          Component: (await import('@/features/projects/ProjectDetailPage')).ProjectDetailPage,
        }),
      },
      {
        path: 'verkauf',
        lazy: async () => ({
          Component: (await import('@/features/sales/SalesDocumentsPage')).SalesDocumentsPage,
        }),
      },
      {
        path: 'verkauf/analyse',
        lazy: async () => ({
          Component: (await import('@/features/sales/SalesAnalyticsPage')).SalesAnalyticsPage,
        }),
      },
      {
        path: 'vertriebspipeline',
        lazy: async () => ({
          Component: (await import('@/features/sales/SalesPipelinePage')).SalesPipelinePage,
        }),
      },
      {
        path: 'verkauf/:id',
        lazy: async () => ({
          Component: (await import('@/features/sales/SalesDocumentRedirect')).SalesDocumentRedirect,
        }),
      },
      {
        path: 'crm',
        lazy: async () => ({
          Component: (await import('@/features/crm/CustomersPage')).CustomersPage,
        }),
      },
      {
        path: 'crm/analyse',
        lazy: async () => ({
          Component: (await import('@/features/crm/CrmAnalyticsPage')).CrmAnalyticsPage,
        }),
      },
      {
        path: 'crm/lieferadressen',
        lazy: async () => ({
          Component: (await import('@/features/crm/DeliveryAddressesPage')).DeliveryAddressesPage,
        }),
      },
      {
        path: 'crm/lieferadressen/:id',
        lazy: async () => ({
          Component: (await import('@/features/crm/DeliveryAddressDetailPage')).DeliveryAddressDetailPage,
        }),
      },
      {
        path: 'crm/ansprechpartner',
        lazy: async () => ({
          Component: (await import('@/features/crm/ContactsDirectoryPage')).ContactsDirectoryPage,
        }),
      },
      {
        path: 'crm/ansprechpartner/:id',
        lazy: async () => ({
          Component: (await import('@/features/crm/ContactDetailPage')).ContactDetailPage,
        }),
      },
      {
        path: 'crm/:id',
        lazy: async () => ({
          Component: (await import('@/features/crm/CustomerDetailPage')).CustomerDetailPage,
        }),
      },
      {
        path: 'referenzen',
        lazy: async () => ({
          Component: (await import('@/features/projects/ReferencesPage')).ReferencesPage,
        }),
      },
      {
        path: 'referenzen/karte',
        lazy: async () => ({
          Component: (await import('@/features/projects/ReferencesMapPage')).ReferencesMapPage,
        }),
      },
      {
        path: 'einkauf',
        lazy: async () => ({
          Component: (await import('@/features/inventory/ArticlesPage')).ArticlesPage,
        }),
      },
      {
        path: 'einkauf/analyse',
        lazy: async () => ({
          Component: (await import('@/features/inventory/InventoryAnalyticsPage')).InventoryAnalyticsPage,
        }),
      },
      {
        path: 'einkauf/:id',
        lazy: async () => ({
          Component: (await import('@/features/inventory/ArticleDetailPage')).ArticleDetailPage,
        }),
      },
      {
        path: 'lager-bewegungen',
        lazy: async () => ({
          Component: (await import('@/features/inventory/StockMovementsPage')).StockMovementsPage,
        }),
      },
      // Materialbedarf (Tech-Lead 2026-07-30): Übersicht je Projekt, Klick öffnet
      // die Bedarfsliste. Löst den Projekt-Unter-Tab „Bedarf / Material" ab.
      {
        path: 'materialbedarf',
        lazy: async () => ({
          Component: (await import('@/features/inventory/MaterialDemandPage')).MaterialDemandPage,
        }),
      },
      {
        path: 'materialbedarf/:projectId',
        lazy: async () => ({
          Component: (await import('@/features/inventory/ProjectMaterialDemandPage')).ProjectMaterialDemandPage,
        }),
      },
      {
        path: 'einkauf/inventur',
        lazy: async () => ({
          Component: (await import('@/features/inventory/InventoryCountsListPage')).InventoryCountsListPage,
        }),
      },
      {
        path: 'einkauf/inventur/:id',
        lazy: async () => ({
          Component: (await import('@/features/inventory/InventoryCountDetailPage')).InventoryCountDetailPage,
        }),
      },
      {
        // „Technik" landet direkt auf den Zeichnungsnummern (keine eigene Übersicht mehr).
        // Alt-Pfad /engineering/zeichnungsnummern bleibt als Alias für Bookmarks bestehen.
        path: 'engineering',
        lazy: async () => ({
          Component: (await import('@/features/engineering/ZeichnungsnummernPage'))
            .ZeichnungsnummernPage,
        }),
      },
      {
        path: 'engineering/zeichnungsnummern',
        lazy: async () => ({
          Component: (await import('@/features/engineering/ZeichnungsnummernPage'))
            .ZeichnungsnummernPage,
        }),
      },
      {
        path: 'engineering/antraege',
        lazy: async () => ({
          Component: (await import('@/features/engineering/AntraegePage')).AntraegePage,
        }),
      },
      {
        path: 'engineering/warengruppen',
        lazy: async () => ({
          Component: (await import('@/features/engineering/WarengruppenPage')).WarengruppenPage,
        }),
      },
      {
        path: 'engineering/zusatzmaterial',
        lazy: async () => ({
          Component: (await import('@/features/inventory/KitOverviewPage')).KitOverviewPage,
        }),
      },
      {
        path: 'engineering/stuecklisten',
        lazy: async () => ({
          Component: (await import('@/features/engineering/StuecklistenPage')).StuecklistenPage,
        }),
      },
      {
        path: 'engineering/stuecklisten/:bomId',
        lazy: async () => ({
          Component: (await import('@/features/engineering/BomEditorPage')).BomEditorPage,
        }),
      },
      {
        path: 'engineering/stuecklisten-vorlagen',
        lazy: async () => ({
          Component: (await import('@/features/engineering/BomTemplatesPage')).BomTemplatesPage,
        }),
      },
      {
        path: 'engineering/stuecklisten-vorlagen/:templateId',
        lazy: async () => ({
          Component: (await import('@/features/engineering/BomTemplateEditorPage')).BomTemplateEditorPage,
        }),
      },
      {
        path: 'engineering/materialcodes',
        lazy: async () => ({
          Component: (await import('@/features/engineering/MaterialcodesPage')).MaterialcodesPage,
        }),
      },
      {
        path: 'beschaffung',
        lazy: async () => ({
          Component: (await import('@/features/procurement/PurchaseOrdersPage')).PurchaseOrdersPage,
        }),
      },
      {
        path: 'beschaffung/analyse',
        lazy: async () => ({
          Component: (await import('@/features/procurement/ProcurementAnalyticsPage')).ProcurementAnalyticsPage,
        }),
      },
      {
        path: 'beschaffung/:id',
        lazy: async () => ({
          Component: (await import('@/features/procurement/PurchaseOrderRedirect')).PurchaseOrderRedirect,
        }),
      },
      {
        path: 'bedarfsvorschau',
        lazy: async () => ({
          Component: (await import('@/features/forecast/ForecastsPage')).ForecastsPage,
        }),
      },
      {
        // Vor „:id" deklariert, damit „analyse" nicht als Listen-ID interpretiert wird.
        path: 'bedarfsvorschau/analyse',
        lazy: async () => ({
          Component: (await import('@/features/forecast/ForecastAnalyticsPage')).ForecastAnalyticsPage,
        }),
      },
      {
        path: 'bedarfsvorschau/:id',
        lazy: async () => ({
          Component: (await import('@/features/forecast/ForecastDetailPage')).ForecastDetailPage,
        }),
      },
      {
        path: 'wareneingang',
        lazy: async () => ({
          Component: (await import('@/features/goods-receipt/GoodsReceiptPage')).GoodsReceiptPage,
        }),
      },
      {
        path: 'wareneingang/:id',
        lazy: async () => ({
          Component: (await import('@/features/goods-receipt/GoodsReceiptDetailPage')).GoodsReceiptDetailPage,
        }),
      },
      {
        path: 'kommissionierung',
        lazy: async () => ({
          Component: (await import('@/features/commissioning/CommissioningPage')).CommissioningPage,
        }),
      },
      {
        path: 'kommissionierung/:id',
        lazy: async () => ({
          Component: (await import('@/features/commissioning/PickingOrderPage')).PickingOrderPage,
        }),
      },
      {
        path: 'transporte',
        lazy: async () => ({
          Component: (await import('@/features/transport/TransportsPage')).TransportsPage,
        }),
      },
      {
        path: 'transporte/:id',
        lazy: async () => ({
          Component: (await import('@/features/transport/TransportDetailPage')).TransportDetailPage,
        }),
      },
      {
        path: 'sperrlager',
        lazy: async () => ({
          Component: (await import('@/features/blocked-stock/BlockedStockPage')).BlockedStockPage,
        }),
      },
      {
        path: 'sperrlager/:id',
        lazy: async () => ({
          Component: (await import('@/features/blocked-stock/BlockedStockDetailPage')).BlockedStockDetailPage,
        }),
      },
      {
        path: 'reporting',
        lazy: async () => ({
          Component: (await import('@/features/reporting/ReportingPage')).ReportingPage,
        }),
      },
      {
        path: 'montage',
        lazy: async () => ({
          Component: (await import('@/features/assembly/AssemblyPage')).AssemblyPage,
        }),
      },
      {
        path: 'buchhaltung',
        lazy: async () => ({
          Component: (await import('@/features/accounting/AccountingPage')).AccountingPage,
        }),
      },
      {
        path: 'hr',
        lazy: async () => ({ Component: (await import('@/features/hr/HrPage')).HrPage }),
      },
      {
        path: 'zeiterfassung',
        lazy: async () => ({
          Component: (await import('@/features/time-tracking/TimeTrackingDashboardPage')).TimeTrackingDashboardPage,
        }),
      },
      {
        // Zeitprotokoll liegt jetzt direkt auf dem Dashboard — alte Links umleiten.
        path: 'zeiterfassung/protokoll',
        element: <Navigate to="/zeiterfassung" replace />,
      },
      {
        path: 'zeiterfassung-jahresuebersicht',
        lazy: async () => ({
          Component: (await import('@/features/time-tracking/MyYearOverviewPage')).MyYearOverviewPage,
        }),
      },
      {
        path: 'zeiterfassung-analyse',
        lazy: async () => ({
          Component: (await import('@/features/time-tracking/TimeTrackingAnalyticsPage')).TimeTrackingAnalyticsPage,
        }),
      },
      {
        path: 'zeiterfassung-stammdaten',
        lazy: async () => ({
          Component: (await import('@/features/time-tracking/MasterDataPage')).MasterDataPage,
        }),
      },
      {
        path: 'zeiterfassung-import',
        lazy: async () => ({
          Component: (await import('@/features/time-tracking/TimeEntryImportPage')).TimeEntryImportPage,
        }),
      },
      {
        path: 'fehlzeiten',
        lazy: async () => ({
          Component: (await import('@/features/leave/LeaveRequestsPage')).LeaveRequestsPage,
        }),
      },
      {
        path: 'urlaubskalender',
        lazy: async () => ({
          Component: (await import('@/features/leave/VacationCalendarPage')).VacationCalendarPage,
        }),
      },
      {
        path: 'reisekosten',
        lazy: async () => ({
          Component: (await import('@/features/travel/TravelExpensesPage')).TravelExpensesPage,
        }),
      },
      {
        path: 'einstellungen',
        lazy: async () => ({
          Component: (await import('@/features/settings/SettingsPage')).SettingsPage,
        }),
      },
      {
        path: 'administration',
        lazy: async () => ({
          Component: (await import('@/features/admin/AdminPage')).AdminPage,
        }),
      },
      {
        // Partnerzugänge fürs Partnerportal (ADR 0085) — eigene Seite unter
        // Administration, weil es Zugänge von AUSSEN sind.
        path: 'partnerzugaenge',
        lazy: async () => ({
          Component: (await import('@/features/admin/PartnersPage')).PartnersPage,
        }),
      },
      {
        path: 'cockpit',
        lazy: async () => ({
          Component: (await import('@/features/system/CockpitPage')).CockpitPage,
        }),
      },
      {
        path: 'benachrichtigungen',
        lazy: async () => ({
          Component: (await import('@/features/notifications/NotificationsPage')).NotificationsPage,
        }),
      },
      {
        path: 'import',
        lazy: async () => ({
          Component: (await import('@/features/import/ImportPage')).ImportPage,
        }),
      },
      {
        path: 'datenbank',
        lazy: async () => ({
          Component: (await import('@/features/database/DatabasePage')).DatabasePage,
        }),
      },
      {
        path: 'audit-log',
        lazy: async () => ({
          Component: (await import('@/features/audit-log/AuditLogPage')).AuditLogPage,
        }),
      },
      {
        path: 'dateien',
        lazy: async () => ({
          Component: (await import('@/features/dms/DmsPage')).DmsPage,
        }),
      },
      {
        path: 'verbesserungen',
        lazy: async () => ({
          Component: (await import('@/features/feedback/FeedbackPage')).FeedbackPage,
        }),
      },
      {
        path: 'changelog',
        lazy: async () => ({
          Component: (await import('@/features/changelog/ChangelogPage')).ChangelogPage,
        }),
      },
      { path: '*', element: <ComingSoonPage title="Seite nicht gefunden" /> },
    ],
  },
  /*
   * Vollbild-Seiten liegen bewusst NEBEN der App-Shell (kein Rahmen, keine Sidebar).
   * Damit erben sie aber auch deren `errorElement` nicht — jede von ihnen trägt es
   * deshalb selbst. Ohne das landete ein Fehler beim Öffnen (allen voran der
   * veraltete Lazy-Chunk nach einem Deploy, siehe RouteErrorBoundary) in der
   * nackten Standard-Fehlerseite des Routers: kein automatisches Neuladen, keine
   * Erklärung. Für den Nutzer sah das aus, als täte der Knopf, der hierher führt,
   * einfach nichts (VB-26019).
   */
  // Vollbild-Beleg-Editor (eigener Tab, ohne App-Shell) — Belegblatt/Werkbank.
  {
    path: '/verkauf/:id/editor',
    errorElement: <RouteErrorBoundary />,
    lazy: async () => ({
      Component: (await import('@/features/sales/SalesDocumentEditorPage')).SalesDocumentEditorPage,
    }),
  },
  {
    path: '/beschaffung/:id/editor',
    errorElement: <RouteErrorBoundary />,
    lazy: async () => ({
      Component: (await import('@/features/procurement/PurchaseOrderEditorPage')).PurchaseOrderEditorPage,
    }),
  },
  // PDF-Vorschau (eigener Tab, ohne App-Shell) — Speichern + Mail senden.
  {
    path: '/verkauf/:id/pdf',
    errorElement: <RouteErrorBoundary />,
    lazy: async () => ({
      Component: (await import('@/features/sales/SalesDocumentPdfPage')).SalesDocumentPdfPage,
    }),
  },
  {
    path: '/beschaffung/:id/pdf',
    errorElement: <RouteErrorBoundary />,
    lazy: async () => ({
      Component: (await import('@/features/procurement/PurchaseOrderPdfPage')).PurchaseOrderPdfPage,
    }),
  },
  // Vollbild-Wareneingangsprüfung (an der Rampe am Tablet, ohne App-Shell).
  {
    path: '/wareneingang/:id/pruefen',
    errorElement: <RouteErrorBoundary />,
    lazy: async () => ({
      Component: (await import('@/features/goods-receipt/GoodsReceiptCheckPage')).GoodsReceiptCheckPage,
    }),
  },
  // Vollbild-Kommissionierung (tablet-optimiert, ohne App-Shell).
  {
    path: '/kommissionierung/:id/picken',
    errorElement: <RouteErrorBoundary />,
    lazy: async () => ({
      Component: (await import('@/features/commissioning/PickingScreenPage')).PickingScreenPage,
    }),
  },
  // Vollbild-Baustellenbericht (auf dem iPad ausfüllbar, ohne App-Shell).
  {
    path: '/montage/:id/bericht',
    errorElement: <RouteErrorBoundary />,
    lazy: async () => ({
      Component: (await import('@/features/assembly/AssemblyReportPage')).AssemblyReportPage,
    }),
  },
  // Vollbild-Projektprotokoll (ADR 0084): wird auf dem iPad auf der Baustelle
  // ausgefüllt und dort unterschrieben — deshalb ohne App-Shell.
  {
    path: '/projekte/:projectId/protokolle/:protocolId',
    errorElement: <RouteErrorBoundary />,
    lazy: async () => ({
      Component: (await import('@/features/projects/ProtocolFormPage')).ProtocolFormPage,
    }),
  },
  // Vollbild-Materialbedarf (Tabellen-Editor, eigener Tab, ohne App-Shell).
  // Pfad bleibt projektbezogen — die Positionen gehören zwingend zu einem Projekt;
  // der Einstieg liegt seit 2026-07-30 im Material-Modul (/materialbedarf).
  {
    path: '/projekte/:id/material-vollbild',
    errorElement: <RouteErrorBoundary />,
    lazy: async () => ({
      Component: (await import('@/features/inventory/MaterialFullscreenPage')).MaterialFullscreenPage,
    }),
  },
  // Vollbild-Referenzenkarte (eigener Tab, ohne App-Shell) — ADR 0063.
  {
    path: '/referenzen/karte/vollbild',
    errorElement: <RouteErrorBoundary />,
    lazy: async () => {
      const { ReferencesMapPage } = await import('@/features/projects/ReferencesMapPage');
      return { Component: () => <ReferencesMapPage fullscreen /> };
    },
  },
]);
